import React, { useState, useEffect, useCallback } from 'react';
import { Input, InputGroup, InputGroupText } from 'reactstrap';
import { useHistory, useLocation } from 'react-router-dom';
import i18n from '@/utils/i18n';

export interface TypeOption {
  id?: string | number;
  type?: string;
  value?: string;
  label?: string;
}

interface TypeFilterProps {
  /** The URL parameter name to use for this filter (e.g., 'type', 'itemType') */
  paramName?: string;
  /** Initial value if no URL parameter is present */
  initialValue?: string;
  /** Custom label text instead of default "Type" */
  label?: string;
  /** Additional CSS classes */
  className?: string;
  /** Callback when type selection changes */
  onChange?: (value: string) => void;
  /** Whether to show the "All" option */
  showAll?: boolean;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Whether the select is loading */
  loading?: boolean;
  /** The type options to display */
  types?: TypeOption[];
  /** Custom text for the "All" option */
  allOptionLabel?: string;
  /** Custom value for the "All" option */
  allOptionValue?: string;
}

/**
 * TypeFilter component that automatically persists type selection to URL parameters.
 * This provides consistent type filtering behavior across the admin interface.
 *
 * Features:
 * - Automatic URL parameter persistence
 * - Back button support (filter state survives navigation)
 * - Consistent UI with reactstrap styling
 * - Flexible type options support
 * - TypeScript support
 */
export const TypeFilter: React.FC<TypeFilterProps> = ({
  paramName = 'type',
  initialValue = 'all',
  label,
  className = '',
  onChange,
  showAll = true,
  disabled = false,
  loading = false,
  types = [],
  allOptionLabel,
  allOptionValue = 'all',
}) => {
  const history = useHistory();
  const location = useLocation();

  // Initialize filter from URL params
  const urlParams = new URLSearchParams(location.search);
  const typeParam = urlParams.get(paramName) || initialValue;
  const [selectedType, setSelectedType] = useState<string>(typeParam);

  // Memoized function to update URL
  const updateUrl = useCallback(
    (typeValue: string) => {
      const params = new URLSearchParams(location.search);
      if (typeValue && typeValue !== allOptionValue) {
        params.set(paramName, typeValue);
      } else {
        params.delete(paramName);
      }
      const newSearch = params.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      // Only update if the URL would actually change
      if (newUrl !== `${location.pathname}${location.search}`) {
        history.replace(newUrl);
      }
    },
    [history, location.pathname, location.search, paramName, allOptionValue],
  );

  // Update URL when selection changes
  useEffect(() => {
    updateUrl(selectedType);
    // Call onChange callback if provided
    if (onChange) {
      onChange(selectedType);
    }
  }, [selectedType, updateUrl, onChange]);

  // Sync state with URL params when URL changes (for back navigation)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const typeParam = urlParams.get(paramName) || initialValue;
    if (typeParam !== selectedType) {
      setSelectedType(typeParam);
    }
  }, [location.search, paramName, initialValue]);

  // Handle type selection change
  const handleTypeChange = (value: string) => {
    setSelectedType(value);
  };

  return (
    <InputGroup className={className}>
      <InputGroupText>{label || i18n.t('Common:type')}</InputGroupText>
      <Input
        type="select"
        value={selectedType}
        onChange={(e) => {
          handleTypeChange(e.currentTarget.value);
        }}
        disabled={loading || disabled}
      >
        {showAll && (
          <option value={allOptionValue}>
            {allOptionLabel || i18n.t('Common:all')}
          </option>
        )}
        {types?.map((typeOption) => {
          // Support both formats: {id, type} and {value, label}
          const key = String(
            typeOption.id || typeOption.value || typeOption.type,
          );
          const displayValue = typeOption.type || typeOption.value || '';
          const displayLabel = typeOption.type || typeOption.label || '';

          return (
            <option key={key} value={displayValue}>
              {displayLabel}
            </option>
          );
        })}
      </Input>
    </InputGroup>
  );
};

export default TypeFilter;
